import { BaseFormatAdapter } from './base-format.adapter';
import { TabularTypeSettings, TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';
import { DataFlowExpressionUtils } from '../expression.util';
import { BooleanDataFlowType, StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource, SQLSourceOptions } from 'app/model/dataflow/dataflow-source';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { Metamodel } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DataFlowAdapterUtils } from '../adapter.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export abstract class TabularTypeAdapter extends BaseFormatAdapter<TabularTypeSettings> {
    private readonly isTridentMdf = __IS_FABRIC_MDF__;

    protected async toSourceDSL(setting: TabularTypeSettings): Promise<string[]> {
        const props = [];
        if(this.isTridentMdf && !stringIsNullOrEmpty(setting.databaseName?.expression)) {
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.databaseName)}`);
        }

        if (setting.useQuery) {
            props.push(`format: 'query'`);
            props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(setting.query)}`);
        } else if (setting.owner instanceof DataFlowSource && setting.owner.format === SQLSourceOptions.Procedure) {
            const source = setting.owner;
            if (source.storedProcInputs.length) {
                const inputs = source.storedProcInputs
                    .filter(
                        (input) =>
                            !stringIsNullOrEmpty(input.paramValue.expression) ||
                            !stringIsNullOrEmpty(input.paramName.expression)
                    )
                    // eslint-disable-next-line max-len
                    .map(
                        (input) =>
                            `${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                input.paramName as DataFlowExpression,
                                StringDataFlowType.TYPE,
                                true
                            )} -> ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                                input.paramValue as DataFlowExpression,
                                StringDataFlowType.TYPE,
                                true
                            )}`
                    )
                    .join(',');
                if (!!inputs) {
                    props.push(`inputs:[${inputs}]`);
                }
            }
            props.push(`procedureName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(source.storedProcName)}`);
            if (!stringIsNullOrEmpty(source.schemaName.expression)) {
                props.push(`schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(source.schemaName)}`);
            }
            props.push(
                `resultSet: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    source.storedProcResultSet,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(`format: 'procedure'`);
        } else {
            props.push(`format: 'table'`);
            if (setting.tableName.expression) {
                props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
            }
            if (setting.schemaName.expression) {
                props.push(`schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.schemaName)}`);
            }
        }
        return props;
    }

    protected async toSinkDSL(setting: TabularTypeSettings): Promise<string[]> {
        const props = [];
        props.push(`format: 'table'`);

        if(this.isTridentMdf && !stringIsNullOrEmpty(setting.databaseName?.expression)) {
            props.push(`database: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.databaseName)}`);
        }
        if (setting.tableName.expression) {
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        }
        if (setting.schemaName.expression) {
            props.push(`schemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.schemaName)}`);
        }
        const sink = setting.owner as DataFlowSink;
        if (sink.alterRowEnabled) {
            props.push(
                `deletable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowDelete,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `insertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowInsert,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `updateable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowUpdate,
                    BooleanDataFlowType.TYPE
                )}`
            );
            props.push(
                `upsertable: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.allowUpsert,
                    BooleanDataFlowType.TYPE
                )}`
            );
        }
        if (sink.requiresKeyColumns) {
            const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(setting);
            if (keyColumns) {
                props.push(keyColumns);
            }
        }

        if (setting.tableActionOption === TableActionOption.Recreate) {
            props.push('recreate: true');
        }

        if (setting.tableActionOption === TableActionOption.Truncate) {
            props.push(`truncate: true`);
        }

        const linkedService = setting.owner instanceof DataFlowSink && setting.owner.sinkLinkedService;
        const supportSpecifyingTmpDB =
            !!linkedService &&
            (
                await getServiceInstance<LinkedServiceRegistryService>(
                    this.context.contextData,
                    'LinkedServiceRegistryService'
                )
            ).getLinkedServiceConfiguration(Metamodel.modelOf(linkedService).name).dataflowSinkStagingSchema;
        const linkedServiceType = linkedService?.getLinkedServiceType();
        if (
            supportSpecifyingTmpDB &&
            !setting.useTmpDB &&
            ((linkedServiceType !== adfModelTypeMap.snowflake.linkedServiceType &&
                linkedServiceType !== adfModelTypeMap.snowflakeV2.linkedServiceType) ||
                DataFlowAdapterUtils.isStagingSchemaPropertyValidForSnowflake(
                    linkedServiceType,
                    setting?.stagingSchemaName?.expression
                ))
        ) {
            props.push(
                `stagingSchemaName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.stagingSchemaName)}`
            );
        }

        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<TabularTypeSettings> {
        const settings = this._generateSetting(owner);

        if (configs.has('query')) {
            settings.query = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('query'), owner);
            DataFlowExpressionUtils.unescapeConfigValue(settings.query);
            settings.useQuery = true;
            handledConfigs.push('query');
        }
        if (this.isTridentMdf && configs.has('database')) {
            settings.databaseName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('database'), owner);
            handledConfigs.push('database');
        }
        if (configs.has('tableName')) {
            settings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('tableName'), owner);
            settings.useQuery = false;
            handledConfigs.push('tableName');
        }
        if (configs.has('schemaName')) {
            settings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('schemaName'), owner);
            settings.useQuery = false;
            handledConfigs.push('schemaName');
        }
        if (configs.has('procedureName')) {
            owner.storedProcName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('procedureName'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('procedureName');
        }
        if (configs.has('inputs')) {
            owner.storedProcInputs = DataFlowExpressionUtils.parseExpressionMapInDsl(
                configs.get('schemaName'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('inputs');
        }

        return settings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<TabularTypeSettings> {
        const settings = this._generateSetting(owner);
        if (this.isTridentMdf && configs.has('database')) {
            settings.databaseName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('database'), owner);
            handledConfigs.push('database');
        }
        if (configs.has('tableName')) {
            settings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('tableName'), owner);
            settings.useQuery = false;
            handledConfigs.push('tableName');
        }
        if (configs.has('schemaName')) {
            settings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('schemaName'), owner);
            settings.useQuery = false;
            handledConfigs.push('schemaName');
        }
        if (configs.has('deletable')) {
            settings.allowDelete = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('deletable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('deletable');
        }
        if (configs.has('insertable')) {
            settings.allowInsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('insertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('insertable');
        }
        if (configs.has('updateable')) {
            settings.allowUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('updateable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('updateable');
        }
        if (configs.has('upsertable')) {
            settings.allowUpsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('upsertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('upsertable');
        }
        if (configs.has('keys')) {
            const expression = configs.get('keys');
            settings.isListOfKeyColumns = expression.startsWith('[') && expression.endsWith(']');
            settings.keyColumns = settings.isListOfKeyColumns
                ? DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner, StringDataFlowType.TYPE, true)
                : [DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, 'string[]', true)];
            handledConfigs.push('keys');
        }
        if (configs.has('truncate')) {
            settings.tableActionOption =
                configs.get('truncate') === 'true' ? TableActionOption.Truncate : settings.tableActionOption;
            handledConfigs.push('truncate');
        }
        if (configs.has('recreate')) {
            settings.tableActionOption =
                configs.get('recreate') === 'true' ? TableActionOption.Recreate : settings.tableActionOption;
            handledConfigs.push('recreate');
        }

        if (configs.has('stagingSchemaName')) {
            settings.stagingSchemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('stagingSchemaName'),
                owner
            );
            settings.useTmpDB = false;
            handledConfigs.push('stagingSchemaName');
        }

        return settings;
    }

    protected abstract _generateSetting(owner: DataFlowSource | DataFlowSink): TabularTypeSettings;
}
